import SectionHeader from "./SectionHeader";

// Brand palette
const FG = "#FFFFFF";
const ACCENT = "#FF3B30";
const AMBER = "#F59E0B";
const ORANGE = "#FB923C";
const GREEN = "#22c55e";
const BLUE = "#60A5FA";
const GRID = "rgba(255,255,255,0.08)";
const AXIS = "rgba(255,255,255,0.45)";
const MUTED = "rgba(255,255,255,0.55)";

// Per-technology palette (used by BOTH charts so legend/colors match)
const COLORS = {
  Harmonic: BLUE,
  "Small Planetary": AMBER,
  "RV / Cycloidal": ORANGE,
  "Precision Planetary": GREEN,
  LogDrive: ACCENT,
};

// Chart layout (viewBox)
const W = 600;
const H = 380;
const PAD = { top: 28, right: 28, bottom: 56, left: 64 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

// Shared X axis (Efficiency %)
const X_MIN = 75;
const X_MAX = 100;
const X_TICKS = [75, 80, 85, 90, 95, 100];

function makeScale(min, max, size) {
  return (v) => ((v - min) / (max - min)) * size;
}

function ChartFrame({ title, yLabel, yMax, yTicks, children }) {
  const sx = makeScale(X_MIN, X_MAX, PLOT_W);
  const sy = makeScale(0, yMax, PLOT_H);

  return (
    <div className="border border-white/10 bg-[#0a0a0a]">
      <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/55">
          {title}
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 bg-[#FF3B30]" />
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/55">
            benchmark
          </span>
        </div>
      </div>
      <div className="p-3">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height="auto"
          role="img"
          aria-label={title}
        >
          {/* gridlines */}
          {yTicks.map((t) => {
            const y = PAD.top + PLOT_H - sy(t);
            return (
              <line
                key={`gy-${t}`}
                x1={PAD.left}
                x2={PAD.left + PLOT_W}
                y1={y}
                y2={y}
                stroke={GRID}
                strokeWidth="1"
              />
            );
          })}
          {X_TICKS.map((t) => {
            const x = PAD.left + sx(t);
            return (
              <line
                key={`gx-${t}`}
                x1={x}
                x2={x}
                y1={PAD.top}
                y2={PAD.top + PLOT_H}
                stroke={GRID}
                strokeWidth="1"
              />
            );
          })}

          {/* axes */}
          <line
            x1={PAD.left}
            x2={PAD.left + PLOT_W}
            y1={PAD.top + PLOT_H}
            y2={PAD.top + PLOT_H}
            stroke={AXIS}
            strokeWidth="1"
          />
          <line
            x1={PAD.left}
            x2={PAD.left}
            y1={PAD.top}
            y2={PAD.top + PLOT_H}
            stroke={AXIS}
            strokeWidth="1"
          />

          {/* y ticks + labels */}
          {yTicks.map((t) => {
            const y = PAD.top + PLOT_H - sy(t);
            return (
              <g key={`yt-${t}`}>
                <line
                  x1={PAD.left - 4}
                  x2={PAD.left}
                  y1={y}
                  y2={y}
                  stroke={AXIS}
                />
                <text
                  x={PAD.left - 8}
                  y={y + 4}
                  fontFamily="JetBrains Mono, monospace"
                  fontSize="11"
                  fill={MUTED}
                  textAnchor="end"
                >
                  {t}
                </text>
              </g>
            );
          })}

          {/* x ticks + labels */}
          {X_TICKS.map((t) => {
            const x = PAD.left + sx(t);
            return (
              <g key={`xt-${t}`}>
                <line
                  x1={x}
                  x2={x}
                  y1={PAD.top + PLOT_H}
                  y2={PAD.top + PLOT_H + 4}
                  stroke={AXIS}
                />
                <text
                  x={x}
                  y={PAD.top + PLOT_H + 18}
                  fontFamily="JetBrains Mono, monospace"
                  fontSize="11"
                  fill={MUTED}
                  textAnchor="middle"
                >
                  {t}
                </text>
              </g>
            );
          })}

          {/* axis labels */}
          <text
            x={PAD.left + PLOT_W / 2}
            y={H - 12}
            fontFamily="JetBrains Mono, monospace"
            fontSize="11"
            fill={FG}
            textAnchor="middle"
            letterSpacing="2"
          >
            EFFICIENCY %
          </text>
          <text
            x={-PAD.top - PLOT_H / 2}
            y={16}
            fontFamily="JetBrains Mono, monospace"
            fontSize="11"
            fill={FG}
            textAnchor="middle"
            transform="rotate(-90)"
            letterSpacing="2"
          >
            {yLabel}
          </text>

          {children({ sx, sy })}
        </svg>
      </div>
    </div>
  );
}

// --- DATA ---
// All "New Drive" values relabelled as LogDrive.
// Competitor curves drawn as smooth fits per the source benchmark chart.

const POWER = {
  yMax: 30,
  yTicks: [0, 5, 10, 15, 20, 25, 30],
  yLabel: "POWER DENSITY  kW / kg",
  series: [
    {
      name: "Harmonic",
      color: COLORS.Harmonic,
      width: 2,
      marker: true,
      points: [
        [78, 0.5], [82, 0.7], [85, 1.0], [88, 1.2],
        [90, 1.5], [92, 1.8], [94, 2.0], [96, 2.2], [98, 2.5],
      ],
    },
    {
      name: "Small Planetary",
      color: COLORS["Small Planetary"],
      width: 2,
      marker: true,
      points: [
        [82, 1.5], [85, 2.0], [88, 2.5], [91, 3.0], [94, 3.5],
      ],
    },
    {
      name: "RV / Cycloidal",
      color: COLORS["RV / Cycloidal"],
      width: 2,
      marker: true,
      points: [
        [83, 2.5], [86, 3.0], [89, 3.5], [91, 4.0],
      ],
    },
    {
      name: "Precision Planetary",
      color: COLORS["Precision Planetary"],
      width: 2,
      marker: true,
      points: [
        [92, 4.0], [94, 4.5], [96, 5.0], [98, 5.5],
      ],
    },
    {
      name: "LogDrive",
      color: COLORS.LogDrive,
      width: 3,
      marker: true,
      points: [
        [79, 17.5], [82, 20.0], [85, 22.5], [87, 25.0],
        [89, 27.5], [91, 29.0], [92, 29.5],
      ],
    },
  ],
};

const TORQUE = {
  yMax: 300,
  yTicks: [0, 50, 100, 150, 200, 250, 300],
  yLabel: "TORQUE DENSITY  Nm / kg",
  series: [
    {
      name: "Harmonic",
      color: COLORS.Harmonic,
      dot: { shape: "diamond", fill: COLORS.Harmonic },
      points: [[80, 50]],
    },
    {
      name: "Small Planetary",
      color: COLORS["Small Planetary"],
      dot: { shape: "star", fill: COLORS["Small Planetary"] },
      points: [[85, 50]],
    },
    {
      name: "RV / Cycloidal",
      color: COLORS["RV / Cycloidal"],
      dot: { shape: "square", fill: COLORS["RV / Cycloidal"] },
      points: [[88, 75]],
    },
    {
      name: "Precision Planetary",
      color: COLORS["Precision Planetary"],
      dot: { shape: "square", fill: COLORS["Precision Planetary"] },
      points: [[95, 75]],
    },
    {
      name: "LogDrive",
      color: COLORS.LogDrive,
      width: 3,
      marker: true,
      points: [[79, 260], [82, 265], [85, 270], [88, 275], [91, 275]],
    },
  ],
};

function pathFromPoints(pts, sx, sy) {
  if (!pts.length) return "";
  return pts
    .map((p, i) => {
      const x = PAD.left + sx(p[0]);
      const y = PAD.top + PLOT_H - sy(p[1]);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");
}

function MarkerShape({ shape, x, y, fill }) {
  const s = 5.5;
  if (shape === "diamond") {
    return (
      <polygon
        points={`${x},${y - s} ${x + s},${y} ${x},${y + s} ${x - s},${y}`}
        fill={fill}
        stroke="#000"
        strokeWidth="0.5"
      />
    );
  }
  if (shape === "square") {
    return (
      <rect
        x={x - s}
        y={y - s}
        width={s * 2}
        height={s * 2}
        fill={fill}
        stroke="#000"
        strokeWidth="0.5"
      />
    );
  }
  if (shape === "star") {
    const r1 = 7;
    const r2 = 3;
    const pts = [];
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? r1 : r2;
      const a = (Math.PI / 5) * i - Math.PI / 2;
      pts.push(`${(x + r * Math.cos(a)).toFixed(2)},${(y + r * Math.sin(a)).toFixed(2)}`);
    }
    return <polygon points={pts.join(" ")} fill={fill} stroke="#000" strokeWidth="0.5" />;
  }
  return <circle cx={x} cy={y} r={s} fill={fill} stroke="#000" strokeWidth="0.5" />;
}

function Series({ s, sx, sy }) {
  const pts = s.points.map(([px, py]) => [
    PAD.left + sx(px),
    PAD.top + PLOT_H - sy(py),
  ]);

  return (
    <g>
      {s.points.length > 1 && (
        <path
          d={pathFromPoints(s.points, sx, sy)}
          fill="none"
          stroke={s.color}
          strokeWidth={s.width || 1.4}
          strokeDasharray={s.dash || ""}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {pts.map(([x, y], i) => {
        const k = `${s.name}-${s.points[i][0]}-${s.points[i][1]}`;
        return s.dot ? (
          <MarkerShape key={k} shape={s.dot.shape} x={x} y={y} fill={s.dot.fill} />
        ) : s.marker ? (
          <circle
            key={k}
            cx={x}
            cy={y}
            r={s.name === "LogDrive" ? 4.5 : 3}
            fill={s.color}
            stroke="#050505"
            strokeWidth="1.2"
          />
        ) : null;
      })}
    </g>
  );
}

function Legend({ series }) {
  return (
    <div className="px-5 pt-3 pb-5 grid grid-cols-2 sm:grid-cols-3 gap-2 border-t border-white/10">
      {series.map((s) => {
        const isLog = s.name === "LogDrive";
        return (
          <div
            key={s.name}
            className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.18em]"
            style={{ color: isLog ? s.color : "rgba(255,255,255,0.75)" }}
          >
            {s.dot ? (
              <svg width="18" height="12" viewBox="-9 -6 18 12">
                <line x1="-9" y1="0" x2="9" y2="0" stroke={s.color} strokeWidth="2" />
                <MarkerShape shape={s.dot.shape} x={0} y={0} fill={s.dot.fill} />
              </svg>
            ) : (
              <span
                className="inline-block"
                style={{
                  width: "20px",
                  height: isLog ? "3px" : "2px",
                  background: s.color,
                }}
              />
            )}
            <span>{s.name}</span>
          </div>
        );
      })}
    </div>
  );
}

function LogDriveCallout({ data, sx, sy }) {
  // Place the "LogDrive" label slightly above the rightmost LogDrive point.
  const log = data.series.find((s) => s.name === "LogDrive");
  const last = log.points[log.points.length - 1];
  const x = PAD.left + sx(last[0]);
  const y = PAD.top + PLOT_H - sy(last[1]);
  return (
    <g>
      <line
        x1={x}
        y1={y}
        x2={x + 28}
        y2={y - 22}
        stroke={ACCENT}
        strokeWidth="1"
        strokeOpacity="0.7"
      />
      <text
        x={x + 32}
        y={y - 22}
        fontFamily="JetBrains Mono, monospace"
        fontSize="11"
        fill={ACCENT}
        fontWeight="700"
        letterSpacing="1.5"
      >
        LOGDRIVE
      </text>
    </g>
  );
}

export default function BenchmarkChart() {
  return (
    <section
      className="relative py-24 md:py-32 border-y border-white/5 bg-[#050505]"
      data-testid="tech-benchmark"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <SectionHeader
            kicker="/ benchmark"
            title={
              <>
                Power and torque<br />density vs. the field.
              </>
            }
            lead="LogDrive plotted against harmonic, RV / cycloidal, small planetary and precision planetary reducers — the four architectures partners benchmark against today. Both metrics are step-function improvements, not incremental."
          />
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-white/50">
            / source · internal benchmark
          </div>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          <div data-testid="benchmark-power">
            <ChartFrame
              title="Power density vs. efficiency"
              yLabel={POWER.yLabel}
              yMax={POWER.yMax}
              yTicks={POWER.yTicks}
            >
              {({ sx, sy }) => (
                <>
                  {POWER.series.map((s) => (
                    <Series key={s.name} s={s} sx={sx} sy={sy} />
                  ))}
                  <LogDriveCallout data={POWER} sx={sx} sy={sy} />
                </>
              )}
            </ChartFrame>
            <Legend series={POWER.series} />
          </div>

          <div data-testid="benchmark-torque">
            <ChartFrame
              title="Torque density vs. efficiency"
              yLabel={TORQUE.yLabel}
              yMax={TORQUE.yMax}
              yTicks={TORQUE.yTicks}
            >
              {({ sx, sy }) => (
                <>
                  {TORQUE.series.map((s) => (
                    <Series key={s.name} s={s} sx={sx} sy={sy} />
                  ))}
                  <LogDriveCallout data={TORQUE} sx={sx} sy={sy} />
                </>
              )}
            </ChartFrame>
            <Legend series={TORQUE.series} />
          </div>
        </div>

        {/* Headline takeaway tiles */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-white/10 p-5 bg-black">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50">
              Power density
            </div>
            <div className="mt-2 font-display font-black text-3xl tracking-tighter">
              <span className="text-[#FF3B30]">~5×</span> precision planetary
            </div>
            <p className="mt-2 text-white/60 text-sm leading-relaxed">
              ~29 kW/kg vs. ~5 kW/kg in the same efficiency band.
            </p>
          </div>
          <div className="border border-white/10 p-5 bg-black">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50">
              Torque density
            </div>
            <div className="mt-2 font-display font-black text-3xl tracking-tighter">
              <span className="text-[#FF3B30]">3 – 4×</span> the field
            </div>
            <p className="mt-2 text-white/60 text-sm leading-relaxed">
              ~270 Nm/kg sustained across the operating envelope.
            </p>
          </div>
          <div className="border border-white/10 p-5 bg-black">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50">
              Efficiency band
            </div>
            <div className="mt-2 font-display font-black text-3xl tracking-tighter">
              <span className="text-[#FF3B30]">75 – 95%</span>
            </div>
            <p className="mt-2 text-white/60 text-sm leading-relaxed">
              Performance maintained where competing architectures collapse.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
